// Reference EnhancedMultiSelectAdapter backed by downshift's useCombobox +
// useMultipleSelection hooks — a select2-style control: selected values as
// removable chips, a type-to-filter input, and a dropdown of remaining
// options. Shipped as a SEPARATE entry
// (`@scientist/dynamic-form-renderer/enhanced-select`) so apps that don't use
// it never pull downshift into the core bundle. `downshift` is an
// optionalDependency; a host wires this adapter via the renderer's
// `enhancedSelectAdapter` prop.
//
// downshift owns the hard parts (combobox/listbox ARIA wiring,
// aria-activedescendant, keyboard navigation, chip focus + backspace
// removal); this module only renders Bootstrap 5 markup, so there is no
// styling runtime and no <style> injection. Values stay JSON-typed
// throughout — the adapter matches and emits raw option values, never their
// stringified DOM keys.

import { useState } from 'react'
import { useCombobox, useMultipleSelection } from 'downshift'
import type { EnhancedMultiSelectProps } from '../rendererContext'
import type { Option } from '../types'
import { optionKey, valuesEqual } from '../fields/optionUtils'

function labelFor(options: Option[], value: unknown): string {
  return options.find((opt) => valuesEqual(opt.value, value))?.label ?? optionKey(value)
}

export function DownshiftMultiSelect({
  id,
  options,
  selected,
  disabled,
  placeholder,
  hasErrors,
  ariaInvalid,
  ariaRequired,
  ariaDescribedBy,
  maxItems,
  classNames,
  onChange,
  onBlur,
}: EnhancedMultiSelectProps) {
  const [inputValue, setInputValue] = useState('')
  const atMax = maxItems != null && selected.length >= maxItems

  // Menu shows the options not yet selected, narrowed by the filter text
  // (select2's local search). At the max_items cap nothing is addable.
  const query = inputValue.trim().toLowerCase()
  const items = atMax
    ? []
    : options.filter(
        (opt) =>
          !selected.some((value) => valuesEqual(value, opt.value)) &&
          (query === '' || opt.label.toLowerCase().includes(query)),
      )

  const { getSelectedItemProps, getDropdownProps, removeSelectedItem } =
    useMultipleSelection<unknown>({
      selectedItems: selected,
      onStateChange({ selectedItems, type }) {
        switch (type) {
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            onChange(selectedItems ?? [])
            break
          default:
            break
        }
      },
    })

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
  } = useCombobox<Option>({
    id,
    items,
    itemToString: (item) => item?.label ?? '',
    inputValue,
    selectedItem: null,
    defaultHighlightedIndex: 0,
    stateReducer(_state, { changes, type }) {
      switch (type) {
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          // Keep the menu open and the highlight in place for rapid multi-pick.
          return { ...changes, isOpen: true, highlightedIndex: _state.highlightedIndex }
        default:
          return changes
      }
    },
    onStateChange({ inputValue: nextInput, selectedItem, type }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(nextInput ?? '')
          break
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.InputBlur:
          if (selectedItem != null) {
            onChange([...selected, selectedItem.value])
            setInputValue('')
          }
          break
        default:
          break
      }
    },
  })

  const containerClass = [
    classNames.enhancedSelectContainer ??
      'form-control d-flex flex-wrap align-items-center gap-1',
    hasErrors ? 'is-invalid' : '',
    disabled ? 'pe-none bg-light' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="position-relative">
      <div className={containerClass}>
        {selected.map((value, index) => {
          const label = labelFor(options, value)
          return (
            <span
              key={optionKey(value)}
              className={
                classNames.enhancedSelectChip ??
                'border d-inline-flex align-items-center gap-1 py-1 px-2'
              }
              {...getSelectedItemProps({ selectedItem: value, index })}
            >
              {label}
              <button
                type="button"
                className={classNames.enhancedSelectChipRemove ?? 'btn-close'}
                style={{ fontSize: '0.6em', minWidth: '1.5em' }}
                aria-label={`Remove ${label}`}
                disabled={disabled}
                onClick={(event) => {
                  // Don't bubble into the chip's own focus/keydown handling.
                  event.stopPropagation()
                  removeSelectedItem(value)
                }}
              />
            </span>
          )
        })}
        <input
          className={
            classNames.enhancedSelectSearchInput ?? 'border-0 flex-grow-1 p-0 shadow-none'
          }
          style={{ outline: 'none', minWidth: '6rem', backgroundColor: 'transparent' }}
          {...getInputProps(
            getDropdownProps({
              // downshift would derive `${id}-input`; force the exact field id
              // so the FieldShell label's htmlFor and focusField target it.
              id,
              preventKeyAction: isOpen,
              disabled,
              placeholder: selected.length === 0 ? placeholder : undefined,
              'aria-invalid': ariaInvalid,
              'aria-required': ariaRequired,
              'aria-describedby': ariaDescribedBy,
              onBlur,
            }),
          )}
        />
        <button
          type="button"
          className="border-0 bg-transparent p-0 ms-auto text-secondary h-auto"
          aria-label="toggle menu"
          disabled={disabled}
          {...getToggleButtonProps()}
        >
          <span aria-hidden="true">{isOpen ? '▴' : '▾'}</span>
        </button>
      </div>
      <ul
        className={[
          classNames.enhancedSelectMenu ?? 'dropdown-menu w-100 mt-1',
          isOpen ? 'show' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ maxHeight: '16rem', overflowY: 'auto' }}
        {...getMenuProps()}
      >
        {isOpen && items.length === 0 ? (
          <li className="dropdown-item disabled text-muted" aria-disabled="true">
            {atMax ? `Maximum of ${maxItems} selections` : 'No results found'}
          </li>
        ) : null}
        {isOpen
          ? items.map((item, index) => (
              <li
                key={optionKey(item.value)}
                className={[
                  classNames.enhancedSelectOption ?? 'dropdown-item',
                  index === highlightedIndex
                    ? (classNames.enhancedSelectOptionHighlighted ?? 'active')
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ cursor: 'pointer' }}
                {...getItemProps({ item, index })}
              >
                {item.label}
              </li>
            ))
          : null}
      </ul>
    </div>
  )
}
